<template>
    <div
        v-if="iframeName !== ''"
        class="iframe-template"
    >
        <div
            :class="['iframe-header',
                     displaySmallOrExtraSmall ? 'iframe-header-small' : '']"
        >
            <div class="iframe-header-name">
                <h2 class="iframe-header-part">
                    {{ headerText }}<span class="iframe-header-part"> {{ additionalHeaderText }}</span>
                </h2>
            </div>
            <div
                v-if="iframeName !== 'NotFound'"
                class="iframe-header-actions"
            >
                <img
                    class="iframe-action-icon"
                    :src="arrowRight"
                    :alt="arrowRightAlt"
                >
                <router-link
                    v-slot="{ href, navigate }"
                    :to="etusivuLink.to"
                    class="routerlink"
                    custom
                >
                    <a
                        :href="href"
                        class="navigation-font"
                        target="_blank"
                        @click="navigate"
                    >
                        {{ $t("App.IframeTemplate.ExternalLink") }}
                        <img
                            class="iframe-external-icon"
                            :src="externalLink"
                            :alt="externalLinkAlt"
                        >
                        <span class="visually-hidden">{{ linkOpensNewTabText }}</span>
                    </a>
                </router-link>
            </div>
        </div>
        <div
            v-if="!notFound"
            :class="['iframe-body',
                     displaySmallOrExtraSmall ? 'iframe-body-s-xs' : '']"
        >
            <div class="iframe-map">
                <lmap
                    ref="map"
                    :disable-layer-events="disableLayerEvents"
                /> <!-- TODO OKo: Pitäisikö käyttää omaa map-komponenttia, josta olisi riisuttu kaikki turha pois, jota iframeissa ei tarvita?-->
            </div>
            <!-- Näytetään joko rightslot tai legend riippuen sivun sisällöstä -->
            <div
                v-if="showRightSlot"
                :class="['iframe-legend',
                         displayType === displayLarge || displayType === displayMedium ? 'iframe-legend-l-m' : '',
                         displaySmallOrExtraSmall ? 'iframe-legend-s-xs' : '' ]"
            >
                <div>
                    <right-slot />
                </div>
            </div>
            <div
                v-if="!showRightSlot"
                :class="['iframe-legend',
                         displayType === displayLarge || displayType === displayMedium ? 'iframe-legend-l-m' : '',
                         displaySmallOrExtraSmall ? 'iframe-legend-s-xs' : '' ]"
            >
                <div
                    v-if="rightSlotData != null"
                    class="iframe-right-slot-header"
                >
                    <div class="header">
                        <p v-if="rightSlotData.dataUpdatedDate != null && disableLayerEvents">
                            <!-- eslint-disable vue/no-v-html -->
                            <span v-html="rightSlotData.dataUpdatedDate" />
                            <!--eslint-enable-->
                        </p>
                    </div>
                </div>
                <map-legend
                    :data="legend"
                    :title-name="iframeType"
                />
            </div>
        </div>
        <div v-else>
            <not-found />
        </div>
        <div class="iframe-footer" />
    </div>
</template>

<script lang="ts">
    import { Component, Vue, Watch } from "vue-facing-decorator"
    import MapClass from "@/map/Map.vue"
    import MapLegend from "@/map/MapLegend.vue"
    import NotFound from "@/views/NotFound.vue"
    import DynamicRightSlotData from "@/models/dynamicrighslotdata"
    import PageWhitelistUtils from "@/helpers/pageWhitelistUtils"
    import RightSlotTemplate from "@/templates/RightSlotTemplate.vue"

    @Component({
        components: {
            lmap: MapClass,
            "map-legend": MapLegend,
            "not-found": NotFound,
            "right-slot": RightSlotTemplate
        }
    })
    export default class IframeTemplate extends Vue {
        iframeName: string = ""
        iframeType: string = ""
        headerText: string | undefined = ""
        additionalHeaderText: string | undefined = ""
        notFound: boolean = false

        etusivuLink = {
            to: "/"
        }

        // TODO miten kielisys hoidetaan iframessa?

        created(): void { // TODO OKo: Onko mounted optimi lifecycle eventti?
            // Jos iframeName muuttuja löytyy, asetetaan tyyppi
            // Huom tyypin on vastattava Map.handleData tyyppejä koska ko. tyypin mukaan muodostetaan kartan datanäkymä ja legenda
            if (this.$route.params.iframeName) {
                this.iframeName = this.$route.params.iframeName as string
            }

            // Saatetaan muuttaa mounted metodissa
            this.headerText = this.$t(`App.Iframe.Header.${this.iframeName}`)

            // Jos iframeName on tyhjä, asetetaan not found ja piilotetaan sisältö. Whitelist-tarkastus tehdään mounted()-metodissa.
            if (this.iframeName === "") {
                this.iframeName = "NotFound"
                this.notFound = true
            }
            else {
                this.notFound = false
            }
        }

        async mounted(): Promise<void> {
            if (this.notFound === false) {
                this.$store.dispatch("setLoading", { name: "iframe", value: true })

                // Tehdään apikutsu, jolla haetaan kartan data
                const apiCallBasePart: string = this.getApiCallBasePart(this.iframeName)
                let iframeType = this.iframeName
                if (this.iframeName.startsWith("MobiiliverkonKattavuus")) {
                    iframeType = "MobiiliverkonKattavuus"
                    this.headerText = (this.$t(`App.Iframe.Header.${this.iframeName}`) ?? "")
                        + " " + this.$t("App.LaajakaistaJaPuhelin." + this.$route.query.tekniikka)
                        + " " + this.$t("App.LaajakaistaJaPuhelin." + (this.$route.query.nopeus as string)?.replace(/\./g, ""))
                }
                else if (this.iframeName.startsWith("TieliikenneOnnettomuudet")) {
                    iframeType = "Liikenneonnettomuudet"
                }
                else if (this.iframeName.startsWith("LiikenteenTilastot")) {
                    if (this.$route.query.type !== undefined) {
                        iframeType = this.$route.query.type?.toString() as string
                        this.headerText = this.$t(`App.Iframe.Header.${iframeType}`)

                        if (iframeType === "EnsirekisteroidytHenkiloAutot" || iframeType === "LiikennekaytossaHenkiloAutot") {
                            // Lisätään käyttövoima otsikkoon
                            if (this.$route.query.propulsionType !== undefined) {
                                this.additionalHeaderText = this.$t("App.TieLiikenne.DropDown.Kayttovoimat." + this.$route.query.propulsionType)

                                if (this.$route.query.propulsionType === "LadattavaHybridi") {
                                    this.$route.query.propulsionType = "Ladattavat hybridit"
                                }
                                else if (this.$route.query.propulsionType === "Sahko") {
                                    this.$route.query.propulsionType = "Sähkö"
                                }
                            }
                        }
                    }
                    else {
                        iframeType = ""
                    }
                }
                else if (this.iframeName.startsWith("Infokartta")) {
                    this.headerText = "" // Otsikko tulee infokartan datasta
                    this.$store.dispatch("setRightSlot", "Infokartat")
                }
                else if (this.iframeName.startsWith("KiinteanLaajakaistanSaatavuus")) {
                    iframeType = "KiinteanLaajakaistanSaatavuus"
                }
                else if (this.iframeName.startsWith("KiinteanVerkonSaatavuusRuudut")) {
                    iframeType = "KiinteanVerkonSaatavuusRuuduilla"
                }
                this.iframeType = iframeType

                const pageWhitelistUtils = new PageWhitelistUtils()
                if (!pageWhitelistUtils.isSubjectOnWhiteList(this.iframeType)) {
                    // Sivu ei ole whitelistalla. Ei näytetä.
                    this.iframeName = "NotFound"
                    this.notFound = true
                }
                else {
                    // TODO kyselyn parametrit voi olla suoraan urlissa tai sitten ne voidaan asettaa dataurltoloadille myös erillisenä parametreinä const dataToLoad: { url: string, type: string, params?: URLSearchParams } = { url: "", type: "" }
                    this.$store.dispatch("setDataUrlToLoad", { url: apiCallBasePart + this.iframeName, type: iframeType, params: this.$route.query })
                }

                this.$store.dispatch("setLoading", { name: "iframe", value: false })
            }
        }

        get displayLarge(): string {
            return this.$store.state.displayLarge
        }

        get displayExtraLarge(): string {
            return this.$store.state.displayExtraLarge
        }

        get displayMedium(): string {
            return this.$store.state.displayMedium
        }

        get displaySmall(): string {
            return this.$store.state.displaySmall
        }

        get displayExtraSmall(): string {
            return this.$store.state.displayExtraSmall
        }

        /** Haetaan oikea url josta data haetaan */
        getApiCallBasePart(type: string): string {
            switch (type) {
                case "TODO-Posti":
                    return ""
                case "LupaJaRekisterointitoimipaikat": // tieliikenne
                    return ""
                case "TieliikenneOnnettomuudetKuntaKohtainen":
                case "TieliikenneOnnettomuudetMaakuntaKohtainen":
                case "LiikenteenTilastotKuntaKohtainen":
                case "LiikenteenTilastotMaakuntaKohtainen":
                    return "v1/TieLiikenne/TieliikenteenTilastot/"
                case "Rekisterointitoimipaikat": // vesiliikenne
                    return ""
                case "MobiiliverkonKattavuusKokoSuomi":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "MobiiliverkonKattavuusKuntakohtainen":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "MobiiliverkonKattavuusMaakuntakohtainen":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "Laajakaistahankkeet":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "KiinteanLaajakaistanSaatavuusKokoSuomi":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "KiinteanLaajakaistanSaatavuusKuntakohtainen":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "KiinteanLaajakaistanSaatavuusMaakuntakohtainen":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "KiinteanVerkonSaatavuusRuudut":
                    return "v1/Viestinta/LaajakaistaJaPuhelin/"
                case "TVPeittoalueet":
                    return "v1/TVJaRadio/"
                case "RadioPeittoalueet":
                    return ""
                case "Infokartta":
                    return "v1/Infokartta/"
                default:
                    return ""
            }
        }

        // mahdollinen Legenda on luotu jo samalla kuin on muodostettu kohteet kartalle
        get legend(): string {
            return this.$store.getters.legend
        }

        // ruutukoko
        get displayType(): string {
            return this.$store.getters.displayType
        }

        get displaySmallOrExtraSmall(): boolean {
            return this.displayType === this.displaySmall || this.displayType === this.displayExtraSmall
        }

        get arrowRight(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/css-icons/arrow-blue.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get arrowRightAlt(): string | undefined {
            return this.$t("App.IframeTemplate.ArrowRightAlt")
        }

        get externalLink(): string {
            return this.$store.getters.contentServiceUrl + "/Content/icons/svg-blue/linkki-external.svg" + "?" + this.$store.getters.contentServiceTimeStamp
        }

        get externalLinkAlt(): string | undefined {
            return this.$t("App.Link.UlosJohtavaLinkki")
        }

        get linkOpensNewTabText(): string | undefined {
            return this.$t("App.Link.LinkOpensNewTab")
        }

        get rightSlotData(): DynamicRightSlotData | null {
            return (this.$store.getters.rightSlotData as DynamicRightSlotData)
        }

        get showRightSlot(): boolean {
            // Infokartoilla näytetään rightslot, muilla sivuilla legend
            if (this.iframeName === "Infokartta") {
                return true
            }
            return false
        }

        get iframeTitle(): string {
            return this.$store.getters.iframeTitle
        }

        get disableLayerEvents(): boolean {
            // Disabloidaan karttatasojen eventit sivuilla joilla niille ei ole tarvetta
            switch (this.iframeName) {
                case "Laajakaistahankkeet":
                case "Infokartta":
                    return false
                default:
                    return true
            }
        }

        @Watch("iframeTitle")
        async onIframeTitle(): Promise<void> {
            const iframeTitle = this.$store.getters.iframeTitle
            if (iframeTitle !== "") {
                // Asetetaan headeriin Iframen otsikko
                this.headerText = iframeTitle
            }
        }
    }
</script>

<style scoped>
    .iframe-template {
        height: 100%;
        width: 100%;
        --var-iframe-header-height: 65px;
    }

    .iframe-header {
        height: var(--var-iframe-header-height);
        display: flex;
    }

    .iframe-header-small {
        flex-direction: column;
        height: auto;
    }

        .iframe-header-small .iframe-header-actions {
            padding-top: 0px;
            justify-content: left;
            align-self: unset
        }

        .iframe-header-small .iframe-header-name {
            align-self: unset
        }

    .iframe-header-s-xs {
        flex-wrap: wrap;
        height: auto;
        padding: 20px 0px;
    }

    .iframe-header-name {
        justify-content: left;
        flex-grow: 1;
        padding: 20px;
        align-self: center;
    }

    .iframe-header-name h2 {
        margin: 0;
        font-weight: 500;
    }

        .iframe-header-name h2 > span {
            font-weight: 400;
        }

    @media (min-width: 1100px) {
        .iframe-header-part {
            white-space: nowrap;
            display: inline-flex;
        }
    }

    .iframe-header-actions {
        justify-content: right;
        padding: 20px;
        align-self: center;
    }

    .iframe-footer {
        height: var(--var-iframe-header-height);
        background-color: #F2F4F8;
    }

    .iframe-body {
        display: flex;
        height: calc(100% - var(--var-iframe-header-height))
    }

    .iframe-body-s-xs {
        flex-direction: column;
    }

    .iframe-map {
        flex-grow: 1;
    }

    .iframe-legend {
        width: 500px;
        background-color: white;
    }

    .iframe-legend-l-m {
        width: 383px;
    }

    .iframe-legend-s-xs {
        width: 100%
    }

    .iframe-action-icon {
        margin-right: 15px;
    }

    .iframe-external-icon {
        width: 14px;
        margin-left: 5px;
    }

    .iframe-right-slot-header {
        padding: 40px 20px 0 20px;
    }
</style>
